import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CANONICAL_BOOKS,
  ensureBookAvailable,
  type BsbBook,
} from '@/lib/bible/active';
import { chapterVerseTexts } from '@/lib/scripture-anchor';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import {
  JOURNAL_SEARCH_LIMITS,
  type TrustedJournalVerseText,
} from '@/lib/journal-search';
import type { BibleHighlight } from '@/types/highlight';

const EMPTY_ROWS: readonly TrustedJournalVerseText[] = Object.freeze([]);
const CANONICAL_BOOK_BY_SLUG = new Map(
  CANONICAL_BOOKS.map((book) => [book.slug, book] as const),
);

export const JOURNAL_BIBLE_SEARCH_LIMITS = Object.freeze({
  maxHighlightsScanned: JOURNAL_SEARCH_LIMITS.maxEntries,
  maxAnchors: JOURNAL_SEARCH_LIMITS.maxTrustedVerseTexts,
  maxBooks: CANONICAL_BOOKS.length,
  bookLoadConcurrency: 4,
} as const);

export type JournalBibleSearchScope = Readonly<object>;

export interface JournalBibleSearchPlanMetrics {
  inputHighlightCount: number;
  scannedHighlightCount: number;
  sourceTruncatedCount: number;
  acceptedAnchorCount: number;
  duplicateAnchorCount: number;
  invalidHighlightCount: number;
  requestedBookCount: number;
}

const EMPTY_PLAN_METRICS: JournalBibleSearchPlanMetrics = Object.freeze({
  inputHighlightCount: 0,
  scannedHighlightCount: 0,
  sourceTruncatedCount: 0,
  acceptedAnchorCount: 0,
  duplicateAnchorCount: 0,
  invalidHighlightCount: 0,
  requestedBookCount: 0,
});

export type JournalBibleSearchTextState =
  | {
      kind: 'idle';
      scope: JournalBibleSearchScope;
      rows: readonly TrustedJournalVerseText[];
    }
  | {
      kind: 'loading';
      scope: JournalBibleSearchScope;
      rows: readonly TrustedJournalVerseText[];
    }
  | {
      kind: 'ready';
      scope: JournalBibleSearchScope;
      rows: readonly TrustedJournalVerseText[];
      metrics: JournalBibleSearchPlanMetrics;
    }
  | {
      kind: 'partial';
      scope: JournalBibleSearchScope;
      rows: readonly TrustedJournalVerseText[];
      failedBookCount: number;
      unresolvedAnchorCount: number;
      metrics: JournalBibleSearchPlanMetrics;
    };

export interface JournalBibleSearchTextOptions {
  /** No public Bible reads occur until this becomes true. */
  enabled: boolean;
  /** Owner/auth/source generation supplied by the caller. */
  scope: JournalBibleSearchScope;
}

export type RequestedJournalBibleBook = {
  slug: string;
  chapters: ReadonlyMap<number, ReadonlySet<number>>;
};

export interface JournalBibleSearchPlan {
  books: readonly RequestedJournalBibleBook[];
  metrics: JournalBibleSearchPlanMetrics;
}

/** Pure, bounded request planning exported for the hostile integration probe. */
export function planJournalBibleSearch(
  highlights: readonly BibleHighlight[],
): JournalBibleSearchPlan {
  const scanCount = Math.min(
    highlights.length,
    JOURNAL_BIBLE_SEARCH_LIMITS.maxHighlightsScanned,
  );
  const byBook = new Map<string, Map<number, Set<number>>>();
  let scannedHighlightCount = 0;
  let acceptedAnchorCount = 0;
  let duplicateAnchorCount = 0;
  let invalidHighlightCount = 0;

  for (let index = 0; index < scanCount; index += 1) {
    if (acceptedAnchorCount >= JOURNAL_BIBLE_SEARCH_LIMITS.maxAnchors) break;
    scannedHighlightCount += 1;
    const highlight = highlights[index];
    const canonicalBook = CANONICAL_BOOK_BY_SLUG.get(highlight.bookSlug);
    if (
      !canonicalBook ||
      !Number.isInteger(highlight.chapter) ||
      highlight.chapter <= 0 ||
      highlight.chapter > canonicalBook.chapters ||
      !Number.isInteger(highlight.verse) ||
      highlight.verse <= 0
    ) {
      invalidHighlightCount += 1;
      continue;
    }

    let chapters = byBook.get(highlight.bookSlug);
    if (!chapters) {
      if (byBook.size >= JOURNAL_BIBLE_SEARCH_LIMITS.maxBooks) {
        invalidHighlightCount += 1;
        continue;
      }
      chapters = new Map();
      byBook.set(highlight.bookSlug, chapters);
    }
    let verses = chapters.get(highlight.chapter);
    if (!verses) {
      verses = new Set();
      chapters.set(highlight.chapter, verses);
    }
    if (verses.has(highlight.verse)) {
      duplicateAnchorCount += 1;
      continue;
    }
    verses.add(highlight.verse);
    acceptedAnchorCount += 1;
  }

  const books = CANONICAL_BOOKS.flatMap((book) => {
    const chapters = byBook.get(book.slug);
    return chapters ? [{ slug: book.slug, chapters }] : [];
  });
  return {
    books,
    metrics: {
      inputHighlightCount: highlights.length,
      scannedHighlightCount,
      sourceTruncatedCount: highlights.length - scannedHighlightCount,
      acceptedAnchorCount,
      duplicateAnchorCount,
      invalidHighlightCount,
      requestedBookCount: books.length,
    },
  };
}

type BookLoadTask = {
  run: () => Promise<BsbBook | null>;
  resolve: (book: BsbBook | null) => void;
  reject: (error: unknown) => void;
};

// One module-wide scheduler keeps overlapping hook generations at the same
// hard concurrency bound. Cancelled queued tasks no-op when their turn arrives.
const bookLoadQueue: BookLoadTask[] = [];
let activeBookLoads = 0;

function drainBookLoadQueue(): void {
  while (
    activeBookLoads < JOURNAL_BIBLE_SEARCH_LIMITS.bookLoadConcurrency &&
    bookLoadQueue.length > 0
  ) {
    const task = bookLoadQueue.shift();
    if (!task) return;
    activeBookLoads += 1;
    void Promise.resolve()
      .then(task.run)
      .then(task.resolve, task.reject)
      .finally(() => {
        activeBookLoads -= 1;
        drainBookLoadQueue();
      });
  }
}

function scheduleBookLoad(run: BookLoadTask['run']): Promise<BsbBook | null> {
  return new Promise((resolve, reject) => {
    bookLoadQueue.push({ run, resolve, reject });
    drainBookLoadQueue();
  });
}

type RequestIdentity = {
  scope: JournalBibleSearchScope;
  highlights: readonly BibleHighlight[];
};

type StoredState = JournalBibleSearchTextState &
  RequestIdentity & {
    retryTick: number;
  };

/**
 * Lazily resolves only the canonical, bounded BSB books required by the active
 * highlight corpus. State is synchronously gated by caller scope + source-array
 * identity, while auth checks and cooperative cancellation guard async work.
 */
export function useJournalBibleSearchText(
  highlights: readonly BibleHighlight[],
  { enabled, scope }: JournalBibleSearchTextOptions,
): {
  state: JournalBibleSearchTextState;
  retry: () => void;
} {
  const [retryTick, setRetryTick] = useState(0);
  const [stored, setStored] = useState<StoredState | null>(null);
  const storedRef = useRef<StoredState | null>(null);
  const retry = useCallback(() => setRetryTick((value) => value + 1), []);
  const publish = useCallback((next: StoredState) => {
    storedRef.current = next;
    setStored(next);
  }, []);
  const current =
    stored?.scope === scope && stored.highlights === highlights ? stored : null;

  useEffect(() => {
    if (!enabled || highlights.length === 0) return;

    const previous = storedRef.current;
    const sameIdentity =
      previous?.scope === scope && previous.highlights === highlights;
    if (
      sameIdentity &&
      previous.retryTick === retryTick &&
      (previous.kind === 'ready' || previous.kind === 'partial')
    ) {
      return;
    }

    const identity: RequestIdentity = { scope, highlights };
    const retainedRows = sameIdentity ? previous.rows : EMPTY_ROWS;
    let cancelled = false;
    const authEpoch = getAuthEpoch();
    const ownerId = getAuthUserId();
    const plan = planJournalBibleSearch(highlights);
    const isAttemptCurrent = (): boolean =>
      !cancelled &&
      getAuthEpoch() === authEpoch &&
      getAuthUserId() === ownerId;
    // Publish from a microtask so the effect starts external async work without
    // a synchronous cascading render. The identity guard also covers cleanup
    // that can occur before this microtask runs.
    queueMicrotask(() => {
      if (!isAttemptCurrent()) return;
      publish({
        ...identity,
        kind: 'loading',
        rows: retainedRows,
        retryTick,
      });
    });

    void (async () => {
      const rows: TrustedJournalVerseText[] = [];
      let failedBookCount = 0;
      let cursor = 0;
      const loadNext = async (): Promise<void> => {
        while (isAttemptCurrent()) {
          const index = cursor;
          cursor += 1;
          const requested = plan.books[index];
          if (!requested) return;
          try {
            const book = await scheduleBookLoad(() =>
              isAttemptCurrent()
                ? ensureBookAvailable(requested.slug)
                : Promise.resolve(null),
            );
            if (!book || !isAttemptCurrent()) return;
            for (const [chapter, requestedVerses] of requested.chapters) {
              if (!isAttemptCurrent()) return;
              const blocks = book.chapters[String(chapter)];
              if (!blocks) continue;
              const verseTexts = chapterVerseTexts(blocks);
              for (const verse of requestedVerses) {
                if (!isAttemptCurrent()) return;
                const text = verseTexts.get(verse);
                if (!text) continue;
                rows.push({
                  bookSlug: requested.slug,
                  chapter,
                  verse,
                  text,
                });
              }
            }
          } catch (error) {
            if (!isAttemptCurrent()) return;
            failedBookCount += 1;
            console.warn('[journal] Scripture search text unavailable', error);
          }
        }
      };

      await Promise.all(
        Array.from(
          {
            length: Math.min(
              JOURNAL_BIBLE_SEARCH_LIMITS.bookLoadConcurrency,
              plan.books.length,
            ),
          },
          () => loadNext(),
        ),
      );
      if (!isAttemptCurrent()) return;
      rows.sort(
        (left, right) =>
          (CANONICAL_BOOK_BY_SLUG.get(left.bookSlug)?.order ?? 0) -
            (CANONICAL_BOOK_BY_SLUG.get(right.bookSlug)?.order ?? 0) ||
          left.chapter - right.chapter ||
          left.verse - right.verse,
      );
      const publishedRows = rows.length === 0 ? EMPTY_ROWS : rows;
      const unresolvedAnchorCount = Math.max(
        0,
        plan.metrics.acceptedAnchorCount - publishedRows.length,
      );
      const partial =
        failedBookCount > 0 ||
        unresolvedAnchorCount > 0 ||
        plan.metrics.sourceTruncatedCount > 0 ||
        plan.metrics.invalidHighlightCount > 0;
      publish(
        partial
          ? {
              ...identity,
              kind: 'partial',
              rows: publishedRows,
              failedBookCount,
              unresolvedAnchorCount,
              metrics: plan.metrics,
              retryTick,
            }
          : {
              ...identity,
              kind: 'ready',
              rows: publishedRows,
              metrics: plan.metrics,
              retryTick,
            },
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, highlights, publish, retryTick, scope]);

  if (!enabled) return { state: { kind: 'idle', scope, rows: EMPTY_ROWS }, retry };
  if (highlights.length === 0) {
    return {
      state: {
        kind: 'ready',
        scope,
        rows: EMPTY_ROWS,
        metrics: EMPTY_PLAN_METRICS,
      },
      retry,
    };
  }
  if (current) return { state: current, retry };
  return { state: { kind: 'loading', scope, rows: EMPTY_ROWS }, retry };
}
